// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../axiosInstance';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [suscripcion, setSuscripcion] = useState(null);
  const [loading, setLoading] = useState(true);
  // Valores posibles: 'loading' | 'ok' | 'unauthorized' | 'error'
  const [authStatus, setAuthStatus] = useState('loading');

  const abortRef = useRef(null);
  const inFlightRef = useRef(null);
  const lastFetchedAtRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const clearRetryTimer = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  const parseRetryAfterSec = (err) => {
    const hdr = err?.response?.headers?.['retry-after'];
    if (hdr) {
      const s = parseInt(hdr, 10);
      if (!Number.isNaN(s) && s > 0) return s;
    }
    const detail = err?.response?.data?.detail || '';
    const m = String(detail).match(/(\d+)\s*seg/);
    if (m?.[1]) return parseInt(m[1], 10);
    return 15; // fallback
  };

  const _consumePendingClinicInvite = useCallback(async () => {
    try {
      const token = localStorage.getItem('clinic_invite_token');
      if (!token) return;
      // intenta aceptar
      await axiosInstance.post('/clinics/join/', { token }, { meta: { skipClinicHeader: true } });
      localStorage.removeItem('clinic_invite_token');
    } catch (e) {
      // si falla, no bloquees el login; solo limpia el token para no repetir infinitamente
      localStorage.removeItem('clinic_invite_token');
    }
  }, []);

  const fetchUserWithFallback = useCallback(async (signal) => {
    try {
      // CAMBIO: evitar X-Clinic-Id en auth
      const res = await axiosInstance.get('/auth/user/', { signal, meta: { skipClinicHeader: true } });
      return res.data || null;
    } catch (err) {
      const st = err?.response?.status;
      if (st === 429) {
        await delay(1200);
        // CAMBIO: evitar X-Clinic-Id en auth
        const retry = await axiosInstance.get('/auth/user/', { signal, meta: { skipClinicHeader: true } });
        return retry.data || null;
      }
      if (st === 404 || st === 405) {
        // CAMBIO: evitar X-Clinic-Id en auth
        const res2 = await axiosInstance.get('/user/', { signal, meta: { skipClinicHeader: true } });
        return res2.data || null;
      }
      // fallback extra
      try {
        // CAMBIO: evitar X-Clinic-Id en auth
        const res2 = await axiosInstance.get('/user/', { signal, meta: { skipClinicHeader: true } });
        return res2.data || null;
      } catch (err2) {
        if (err2?.response?.status === 429) {
          await delay(1200);
          // CAMBIO: evitar X-Clinic-Id en auth
          const retry2 = await axiosInstance.get('/user/', { signal, meta: { skipClinicHeader: true } });
          return retry2.data || null;
        }
        throw err2;
      }
    }
  }, []);

  const fetchAuthData = useCallback(
    async ({ force = false } = {}) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        clearRetryTimer();
        setUser(null);
        setSuscripcion(null);
        setAuthStatus('unauthorized');
        setLoading(false);
        return;
      }

      const now = Date.now();
      const CACHE_MS = 20000;
      if (!force && now - lastFetchedAtRef.current < CACHE_MS && user) {
        setAuthStatus('ok');
        setLoading(false);
        return;
      }

      if (inFlightRef.current && !force) {
        try {
          await inFlightRef.current;
        } catch {}
        return;
      }

      // Cancelar peticiones previas y reintentos pendientes
      abortRef.current?.abort();
      clearRetryTimer();

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setAuthStatus('loading');

      const doFetch = (async () => {
        try {
          const baseUser = await fetchUserWithFallback(controller.signal);

          const [medicoRes, susRes] = await Promise.allSettled([
            // CAMBIO: evitar X-Clinic-Id en auth
            axiosInstance.get('/current-medico/', { signal: controller.signal, meta: { skipClinicHeader: true } }),
            axiosInstance.get('/suscripcion/estado/', { signal: controller.signal, meta: { skipClinicHeader: true } }),
          ]);

          let medicoData = null;
          if (medicoRes.status === 'fulfilled') {
            medicoData = medicoRes.value?.data || null;
          } else {
            const st = medicoRes.reason?.response?.status;
            if (st && st !== 404) console.warn('current-medico falló:', st);
          }

          if (susRes.status === 'fulfilled') {
            setSuscripcion(susRes.value?.data || null);
          } else {
            console.warn('suscripcion/estado falló:', susRes.reason?.response?.status);
            setSuscripcion(null);
          }

          setUser({ ...baseUser, medico: medicoData });
          setAuthStatus('ok');
          lastFetchedAtRef.current = Date.now();
        } catch (err) {
          if (controller.signal.aborted) return;
          const status = err?.response?.status;

          if (status === 401) {
            // Limpiar token inválido/expirado para que el usuario pueda hacer login
            console.warn('401 al validar sesión. Limpiando token y redirigiendo al login.');
            localStorage.removeItem('authToken');
            setUser(null);
            setSuscripcion(null);
            setAuthStatus('unauthorized');
          } else if (status === 429) {
            // Respetar Retry-After y reintentar sin "tumbar" la sesión
            console.warn('Rate limited (429) al validar sesión.');
            const waitSec = parseRetryAfterSec(err);

            // Importante: mantener estado "ok" si hay token para no redirigir
            // y permitir que la UI siga operativa mientras reintenta.
            setAuthStatus('ok');
            setLoading(false);

            retryTimeoutRef.current = setTimeout(() => {
              // Reintento forzado
              fetchAuthData({ force: true }).catch(() => {});
            }, waitSec * 1000);
          } else {
            console.warn('Error auth fetch:', status, err);
            // Si hay token, no forzamos logout: la UI puede seguir y se puede reintentar manualmente
            setAuthStatus(localStorage.getItem('authToken') ? 'ok' : 'error');
          }
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        }
      })();

      inFlightRef.current = doFetch;
      try {
        await doFetch;
      } finally {
        if (inFlightRef.current === doFetch) inFlightRef.current = null;
      }
    },
    [fetchUserWithFallback, user]
  );

  useEffect(() => {
    // Optimista: si hay token, evita parpadeo de "deslogueo" hasta validar
    if (localStorage.getItem('authToken')) {
      setAuthStatus('ok');
      setLoading(true);
    } else {
      setAuthStatus('unauthorized');
    }

    fetchAuthData();

    return () => {
      abortRef.current?.abort();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [fetchAuthData]);

  // Reaccionar a cambios de token en otras pestañas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'authToken') {
        if (e.newValue) {
          fetchAuthData({ force: true }).catch(() => {});
        } else {
          // Token eliminado desde otra pestaña
          clearRetryTimer();
          setUser(null);
          setSuscripcion(null);
          setAuthStatus('unauthorized');
          setLoading(false);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchAuthData]);

  const login = async (token) => {
    localStorage.setItem('authToken', token);
    await fetchAuthData({ force: true });
    await _consumePendingClinicInvite();
  };

  const logout = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // CAMBIO: evitar X-Clinic-Id en logout (opcional)
      axiosInstance.post('/auth/logout/', null, { meta: { skipClinicHeader: true } })
        .catch((err) => console.error('Error en logout backend', err));
    }
    clearRetryTimer();
    setUser(null);
    setSuscripcion(null);
    localStorage.removeItem('authToken');
    setAuthStatus('unauthorized');
  }, []);

  const refreshAuthData = useCallback(async () => {
    await fetchAuthData({ force: true });
  }, [fetchAuthData]);

  // Alias booleano para guards: autenticado si hay token y no está 'unauthorized'
  const hasToken = !!localStorage.getItem('authToken');
  const isAuthenticated = hasToken && authStatus !== 'unauthorized';

  return (
    <AuthContext.Provider
      value={{ user, suscripcion, loading, authStatus, isAuthenticated, login, logout, refreshAuthData }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  return context;
}
